import React, { useEffect, useRef, useState } from 'react';
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  createChart,
} from 'lightweight-charts';
import { apiFetch } from './api';

const MA_CONFIG = [
  // Weighted by how much attention each line deserves: the 10MA is the one the
  // screen is built around, so it stays thin but near-black; the 20 and 50 fade
  // back so they read as context rather than competing with it.
  { period: 10, color: 'rgba(61, 31, 0, 0.95)' },
  { period: 20, color: 'rgba(153, 0, 255, 0.55)' },
  { period: 50, color: 'rgba(25, 118, 210, 0.32)' },
];

function simpleMovingAverage(candles, period) {
  let rollingTotal = 0;
  return candles.reduce((points, candle, index) => {
    rollingTotal += candle.close;
    if (index >= period) rollingTotal -= candles[index - period].close;
    if (index >= period - 1) {
      points.push({ time: candle.time, value: rollingTotal / period });
    }
    return points;
  }, []);
}

// TradingView-style candlestick chart for a single link, mirroring finviz2's
// InteractiveChart but against bse2's /candles/:link/:period endpoint (no
// market/symbol split - bse2 only ever has one exchange).
const InteractiveChart = ({ link, period, reloadKey = 0, pickMode, onPricePick, entryPrice, stopPrice }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const maSeriesRef = useRef([]);
  const volumeSeriesRef = useRef(null);
  const entryLineRef = useRef(null);
  const stopLineRef = useRef(null);
  const hasFitOnceRef = useRef(false);
  const pickModeRef = useRef(pickMode);
  const onPricePickRef = useRef(onPricePick);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    pickModeRef.current = pickMode;
    onPricePickRef.current = onPricePick;
  }, [pickMode, onPricePick]);

  // Create the chart and its series exactly once. Never torn down and rebuilt
  // on link/period changes - that's what was causing the visible redraw.
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#475467',
        fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
        fontSize: 9,
      },
      grid: {
        vertLines: { color: '#f0f2f5' },
        horzLines: { color: '#f0f2f5' },
      },
      // A smaller font narrows the axis's own auto-sized width, leaving the
      // candles more room on the right instead of being squeezed by a wide
      // price-label column.
      // Candles had no explicit scaleMargins, so lightweight-charts fell back
      // to its own default top margin - a large blank band above the highest
      // wick. Pin it down close to the top and reserve room at the bottom so
      // the volume histogram (own scale, bottom 18%) doesn't overlap.
      rightPriceScale: { borderColor: '#d0d5dd', minimumWidth: 0, scaleMargins: { top: 0.05, bottom: 0.2 } },
      timeScale: { borderColor: '#d0d5dd', timeVisible: false, rightOffset: 15 },
      crosshair: { vertLine: { color: '#98a2b3' }, horzLine: { color: '#98a2b3' } },
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#12b76a',
      downColor: '#f04438',
      borderUpColor: '#12b76a',
      borderDownColor: '#f04438',
      wickUpColor: '#12b76a',
      wickDownColor: '#f04438',
    });
    candleSeriesRef.current = candleSeries;

    maSeriesRef.current = MA_CONFIG.map(({ color }) =>
      chart.addSeries(LineSeries, {
        color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        title: '',
      })
    );

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeClick((param) => {
      if (!param.point || !pickModeRef.current || !onPricePickRef.current) return;
      const price = candleSeries.coordinateToPrice(param.point.y);
      if (price != null && Number.isFinite(price)) {
        onPricePickRef.current(Number(price.toFixed(4)));
      }
    });

    const applySize = () => {
      if (containerRef.current && chartRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        if (width > 0 && height > 0) {
          // resize(..., forceRepaint) rather than applyOptions - on some
          // mobile WebKit builds, re-creating the chart on a mode toggle
          // (CD -> TV) leaves the canvas blank after an applyOptions-only
          // resize; forcing the repaint reliably redraws it.
          chartRef.current.resize(width, height, true);
        }
      }
    };
    const resizeObserver = new ResizeObserver(applySize);
    resizeObserver.observe(containerRef.current);
    // Some mobile browsers create the chart before the flex layout has
    // settled (container measures 0 at creation) and don't reliably fire
    // ResizeObserver afterwards, leaving the canvas permanently blank -
    // re-measure several more times after mount, plus on orientation
    // change, as a fallback.
    const settleTimers = [50, 150, 300, 600, 1000, 1600].map((delay) => window.setTimeout(applySize, delay));
    window.addEventListener('orientationchange', applySize);
    window.addEventListener('resize', applySize);

    return () => {
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('orientationchange', applySize);
      window.removeEventListener('resize', applySize);
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      maSeriesRef.current = [];
      volumeSeriesRef.current = null;
      entryLineRef.current = null;
      stopLineRef.current = null;
      hasFitOnceRef.current = false;
    };
  }, []);

  // Draws the entry/stop as horizontal price lines so a picked stop is
  // visibly confirmed on the chart, not just dropped into the order form.
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    if (entryLineRef.current) series.removePriceLine(entryLineRef.current);
    if (stopLineRef.current) series.removePriceLine(stopLineRef.current);
    entryLineRef.current = Number(entryPrice) > 0 ? series.createPriceLine({
      price: Number(entryPrice), color: '#175cd3', lineWidth: 2,
      lineStyle: 2, axisLabelVisible: true, title: 'ENTRY',
    }) : null;
    stopLineRef.current = Number(stopPrice) > 0 ? series.createPriceLine({
      price: Number(stopPrice), color: '#d92d20', lineWidth: 2,
      lineStyle: 2, axisLabelVisible: true, title: 'STOP',
    }) : null;
  }, [entryPrice, stopPrice]);

  // Load candle data into the existing series. No chart teardown here, so the
  // current zoom/pan position is left exactly as the user had it.
  useEffect(() => {
    if (!link) return undefined;
    const controller = new AbortController();
    setStatus('loading');

    const load = async () => {
      try {
        const response = await apiFetch(
          `/candles/${encodeURIComponent(link)}/${period}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Unable to load candles');
        const payload = await response.json();
        if (!payload.candles?.length) throw new Error('No candle data');
        if (controller.signal.aborted || !candleSeriesRef.current) return;

        const visibleCandles = period === 'daily' ? (() => {
          const cutoff = new Date(`${payload.candles[payload.candles.length - 1].time}T00:00:00Z`);
          cutoff.setUTCMonth(cutoff.getUTCMonth() - 4);
          const cutoffText = cutoff.toISOString().slice(0, 10);
          return payload.candles.filter((candle) => candle.time > cutoffText);
        })() : payload.candles;

        candleSeriesRef.current.setData(visibleCandles.map((c) => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        })));

        // Averaged over the WHOLE history, then clipped to the visible window.
        // Averaging the sliced array instead is why the 50MA used to begin 50
        // sessions into a chart that is only about 85 sessions long.
        const firstVisible = visibleCandles[0]?.time;
        maSeriesRef.current.forEach((series, index) => {
          const points = simpleMovingAverage(payload.candles, MA_CONFIG[index].period);
          series.setData(firstVisible ? points.filter((p) => p.time >= firstVisible) : points);
        });

        volumeSeriesRef.current?.setData(visibleCandles.map((c) => ({
          time: c.time,
          value: c.volume,
          color: c.close >= c.open ? '#12b76a66' : '#f0443866',
        })));

        if (containerRef.current && chartRef.current) {
          const width = containerRef.current.clientWidth;
          const height = containerRef.current.clientHeight;
          if (width > 0 && height > 0) chartRef.current.resize(width, height, true);
        }
        if (!hasFitOnceRef.current) {
          chartRef.current?.timeScale().fitContent();
          hasFitOnceRef.current = true;
        } else {
          const visibleRange = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (visibleRange && visibleRange.from >= visibleCandles.length) {
            chartRef.current?.timeScale().fitContent();
          }
        }
        setStatus('ready');
      } catch (error) {
        if (error.name !== 'AbortError') setStatus('error');
      }
    };

    load();
    return () => controller.abort();
  }, [link, period, reloadKey]);

  return (
    <div className="chart-shell">
      <div className={`chart-canvas ${pickMode ? 'price-picking' : ''}`} ref={containerRef} />
      {link && status === 'loading' && <div className="chart-status">Loading candles…</div>}
      {link && status === 'error' && <div className="chart-status error">Chart data is unavailable</div>}
    </div>
  );
};

export default InteractiveChart;
